import type { ListingUnit } from "../../services/emailService";
import {
  isAvailableByDate,
  parseSearchCalendarDate,
  type GroupedFloorPlan,
  type GroupedUnit,
  type SearchGroupedProperty,
} from "./groupSearchResults";
import { rentableFloorPlanRange, rentableSelectedUnitPrice } from "./buildFreshShortlistSelections";

type PlanSelection = { floorPlan: GroupedFloorPlan; property: SearchGroupedProperty };
type UnitSelection = PlanSelection & { unit: GroupedUnit };

/** Rebuild email price fields from the selected inventory and CURRENT target.
 * Keep selected identities, including unpriced rows, so one ineligible unit
 * never removes an otherwise eligible property or silently changes selection.
 * This re-evaluates the existing snapshot; it does not claim a live refresh.
 */
export function buildFreshListingSelections(
  selectionMode: "floorplan" | "unit",
  selectedFloorPlans: Map<string, PlanSelection>,
  selectedUnits: Map<string, UnitSelection>,
  targetDate?: Date
): ListingUnit[] {
  const common = ({ floorPlan, property }: PlanSelection) => ({
    property_id: property.property_id,
    property_name: property.property_name,
    neighborhood: property.neighborhood,
    model_name: floorPlan.model_name,
    bedrooms: floorPlan.bedrooms,
    bathrooms: floorPlan.bathrooms,
    square_feet: floorPlan.square_feet,
    leasing_special: property.specials_summary || property.specials,
    photo_url: property.s3_image_url,
    floorplan_url: floorPlan.image_url,
    floor_plan_id: floorPlan.floor_plan_id,
    property_url: property.website || property.url,
  });
  if (selectionMode === "unit") {
    return Array.from(selectedUnits.values()).map((entry) => {
      const { unit, property } = entry;
      const price = property.serve_pricing === true
        ? rentableSelectedUnitPrice(unit, targetDate) : undefined;
      return {
        ...common(entry),
        id: unit.unique_id,
        current_price: price ?? 0,
        status: unit.status,
        available_date: unit.available_date,
        // Explicitly suppress an ineligible row even for legacy status-omitted
        // input. Neither renderer nor the review dialog may revive its price.
        available_count: price === undefined ? 0 : undefined,
      };
    });
  }
  return Array.from(selectedFloorPlans.entries()).map(([id, entry]) => {
    const { floorPlan, property } = entry;
    const units = Array.isArray(floorPlan.units) ? floorPlan.units : [];
    const eligible = units.filter((unit) => isAvailableByDate(unit.status, unit.available_date, targetDate));
    const count = property.serve_pricing === true
      ? (units.length ? eligible.length : floorPlan.available_count) : 0;
    const [min, max] = rentableFloorPlanRange(floorPlan, targetDate);
    const valid = property.serve_pricing === true && count > 0 &&
      typeof min === "number" && Number.isFinite(min) && min > 0 &&
      typeof max === "number" && Number.isFinite(max) && max > 0;
    const dates = eligible.map((unit) => parseSearchCalendarDate(unit.available_date))
      .filter((date): date is Date => date !== null);
    const soonest = dates.length ? new Date(Math.min(...dates.map((date) => date.getTime()))).toISOString() : undefined;
    return {
      ...common(entry),
      id,
      current_price: valid ? min : 0,
      available_date: soonest,
      available_count: count,
      status: count > 0 ? "available" : "unavailable",
      price_display: valid
        ? min === max ? `~$${min.toLocaleString()}` : `$${min.toLocaleString()} – $${max.toLocaleString()}`
        : undefined,
    };
  });
}
